import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  useDisclosure,
  // eslint-disable-next-line no-restricted-imports
  Icon as ChakraIcon,
} from '@chakra-ui/react';
import { LogOut } from 'lucide-react';
import { YapamarLogo } from 'src/components/atoms/YapamarLogo';
import DesktopNav from '../../molecules/DesktopNav';
import DesktopLogin from '../../molecules/DesktopLogin';
import MobileDrawerMenu from '../../molecules/MobileDrawerMenu';
import { IconButton } from 'src/components/atoms/IconButton';
import { Dispatch, SetStateAction } from 'react';
import { Icon } from 'src/components/atoms/Icon';
import { useAuthContext } from 'src/contexts/AuthContext';
import { buttonClickedEvent } from 'src/libs/amp';
// import { IS_DEV } from 'src/constants';
import DesktopSignUp from '../../molecules/DesktopSignUp';
import { IS_DEV } from 'src/constants';

type MarketingPageHeaderProps = {
  onLoginClick?: Dispatch<SetStateAction<boolean>>;
  onSignUpClick?: Dispatch<SetStateAction<boolean>>;
  showLinks?: boolean;
  // Signed-in flows without nav links (onboarding) show a Sign Out action
  showSignOut?: boolean;
  // Pages that follow the color mode (onboarding implies it via showSignOut;
  // /register opts in explicitly). Marketing pages stay light-only.
  allowDarkTheme?: boolean;
};

const MarketingPageHeader: React.FC<MarketingPageHeaderProps> = ({
  onLoginClick = () => {},
  onSignUpClick = () => {},
  showLinks = true,
  showSignOut = false,
  allowDarkTheme = false,
}: MarketingPageHeaderProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { signOut } = useAuthContext();

  // Dark styles apply only where opted in (onboarding, /register): public
  // marketing/landing pages must stay light even for OS-dark visitors.
  const darkHeader =
    useColorModeValue(false, true) && (showSignOut || allowDarkTheme);

  const handleSignOut = () => {
    buttonClickedEvent({
      buttonName: 'sign-out',
    });
    signOut();
  };

  return (
    <>
      <Box
        as="header"
        bg={darkHeader ? 'rgba(12, 13, 15, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
        backdropFilter="blur(10px)"
        sx={{ WebkitBackdropFilter: 'blur(10px)' }}
        w="100%"
        h="64px"
        borderBottom={darkHeader ? '1px solid #2A2D33' : '1px solid #edf2f9'}
        px={showLinks ? { base: 4, md: 12 } : { base: 4, md: 8, lg: 10 }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="fixed"
        top={0}
        left={0}
        zIndex={1000}>
        <Flex
          w="100%"
          maxW={showLinks ? 'container.xl' : '100%'}
          align="center"
          justify="space-between">
          <YapamarLogo theme={darkHeader ? 'white' : 'dark'} />
          {showSignOut && (
            <Flex
              align="center"
              gap={2}
              cursor="pointer"
              color="content.body"
              _hover={{ color: 'content.title' }}
              onClick={handleSignOut}>
              <ChakraIcon as={LogOut} boxSize={4} />
              <Text fontFamily="marketingPage" fontSize="md" fontWeight={500}>
                Sign Out
              </Text>
            </Flex>
          )}
          {showLinks && (
            <>
              <DesktopNav />
              <Box display="flex" gap={6}>
                <DesktopLogin onLoginClick={onLoginClick} />
                {/*{IS_DEV && <DesktopSignUp onSignUpClick={onSignUpClick} />}*/}
              </Box>
              {/* Mobile Hamburger */}
              <IconButton
                aria-label="Open menu"
                bg="gray.100"
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                icon={<Icon iconName="hamburger" fill={'#2D3748'} />}
                variant="ghost"
                color="gray.800"
                display={{ base: 'flex', md: 'none' }}
                onClick={onOpen}
              />
            </>
          )}
        </Flex>
      </Box>
      <MobileDrawerMenu
        onLoginClick={onLoginClick}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
};

export default MarketingPageHeader;
